import { useState, type FormEvent, type ReactNode } from 'react'
import { SignUp } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * AccessGate — a soft 6-digit access-code gate that fronts the entire
 * signed-out flow on the tenant host. Mirrors the marketing-side gate
 * at `get.autoflo.app` so the two pages feel like one product, but
 * this side is purely client-side: a localStorage flag, no signed
 * cookie. The real authorization happens after Clerk on the API side.
 *
 * Why a soft gate is OK: anyone who passes the code still has to be
 * added to a Clerk Organization (the `shops.clerk_org_id` mapping in
 * the Worker) before any API call returns shop-scoped data. The gate
 * just keeps the sign-up/sign-in surface from being trivially
 * crawlable while we're in invite-only beta.
 *
 * Once `localStorage.autoflo_access === 'ok'`, the gate is bypassed
 * forever on that browser — clear localStorage to test the flow again.
 *
 * NOT applied on the admin host (admin.autoflo.app) — internal tool
 * for platform operators who don't need the friction. See App.tsx for
 * the host-aware wrap.
 */

const STORAGE_KEY = 'autoflo_access'
const ACCESS_CODE = '199686'

function readUnlocked(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'ok'
  } catch {
    // Private-mode Safari + similar; treat as locked. The user can
    // still re-enter the code; it just won't persist.
    return false
  }
}

function persistUnlocked() {
  try {
    window.localStorage.setItem(STORAGE_KEY, 'ok')
  } catch {
    // Storage quota / private mode — silently ignore. The unlock
    // still applies for the current page lifecycle via React state.
  }
}

/**
 * Wrap a tree behind the 6-digit access-code gate. Renders the code
 * entry form until the user enters the correct code (or has previously
 * persisted the unlock to localStorage), then renders `children`.
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('auth')
  const [unlocked, setUnlocked] = useState<boolean>(readUnlocked)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (code.trim() === ACCESS_CODE) {
      persistUnlocked()
      setUnlocked(true)
      setError('')
      return
    }
    setError(t('sign_up_gate.invalid_code'))
  }

  if (unlocked) {
    return <>{children}</>
  }

  return (
    <div className="min-h-dvh grid place-items-center bg-brand-tint px-4 py-8">
      <div className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-md text-center">
        {/* Inline brand mark — machine-green per DESIGN.md §1 (the marketing
            gate on get.autoflo.app still carries the old blue until it's
            rebranded separately). */}
        <svg
          viewBox="0 0 240 240"
          aria-hidden="true"
          className="mx-auto mb-4 h-12 w-12"
        >
          <rect width="240" height="240" rx="56" className="fill-saved" />
          <path
            d="M 60,184 L 120,56 L 180,184"
            fill="none"
            stroke="#fff"
            strokeWidth="26"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="86"
            y1="128"
            x2="154"
            y2="128"
            stroke="#fff"
            strokeWidth="22"
            strokeLinecap="round"
          />
          <line
            x1="118"
            y1="208"
            x2="172"
            y2="208"
            stroke="#fff"
            strokeWidth="14"
            strokeLinecap="round"
            opacity="0.55"
          />
        </svg>
        <h1 className="text-xl font-bold tracking-tight text-brand">
          {t('sign_up_gate.title')}
        </h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground leading-relaxed">
          {t('sign_up_gate.lede')}
        </p>
        <form onSubmit={onSubmit} className="text-left" noValidate>
          <Label htmlFor="autoflo-access-code">
            {t('sign_up_gate.label')}
          </Label>
          <Input
            id="autoflo-access-code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            minLength={6}
            autoComplete="off"
            autoFocus
            required
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            className="mt-2 text-center text-lg font-semibold tracking-[0.4em] tabular-nums"
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? 'autoflo-access-code-error' : undefined}
          />
          {error ? (
            <p
              id="autoflo-access-code-error"
              role="alert"
              className="mt-3 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            className="mt-4 w-full bg-brand text-white hover:bg-brand-dark"
          >
            {t('sign_up_gate.submit')}
          </Button>
        </form>
        <p className="mt-6 text-xs text-muted-foreground">
          {t('sign_up_gate.no_code')}{' '}
          <a
            href="mailto:hello@autoflo.ca"
            className="font-medium text-brand hover:underline"
          >
            {t('sign_up_gate.request_access')}
          </a>
          .
        </p>
      </div>
    </div>
  )
}

/**
 * SignUpGate — thin wrapper around `<AccessGate>` that mounts Clerk's
 * `<SignUp />` once the code is entered. Kept as a named export so
 * existing imports / routes don't churn. The substantive gate logic
 * lives in `<AccessGate>` above so we can reuse it to front the whole
 * signed-out flow (SignIn + SignUp), not just /sign-up.
 */
export function SignUpGate() {
  return (
    <AccessGate>
      <div className="min-h-dvh grid place-items-center bg-background px-4 py-8">
        <SignUp />
      </div>
    </AccessGate>
  )
}
